import { LayoutGrid, RefreshCw } from "lucide-react";
import CardImage from "../tool_components/CardImage";
import ChoiceBoardCardPdf from "./ChoiceBoardCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import { Button, Text } from "@core/index";
import useImagePdf from "../pdf/useImagePdf";
import { tC } from "../../utils/TranslateWithContentLanguage";
import { MascotBubble } from "../tool_components/MascotBubble";
import { useState } from "react";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import {
  getGetChoiceBoardQueryKey,
  getGetUserChoiceBoardsQueryKey,
  updateChoiceBoard,
} from "@packages/api/madlenAPI";
import useIsAuthor from "@packages/hooks/useIsAuthor";
import RegeneratingOverlay from "../tool_components/RegenLoadingOverlay";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";

const cardColors = [
  {
    bg: "bg-blue-100",
    text: "text-blue-600",
    dark: "dark:bg-blue-900/50 dark:text-blue-400",
  },
  {
    bg: "bg-purple-100",
    text: "text-purple-600",
    dark: "dark:bg-purple-900/50 dark:text-purple-400",
  },
  {
    bg: "bg-green-100",
    text: "text-green-600",
    dark: "dark:bg-green-900/50 dark:text-green-400",
  },
  {
    bg: "bg-rose-100",
    text: "text-rose-600",
    dark: "dark:bg-rose-900/50 dark:text-rose-400",
  },
  {
    bg: "bg-amber-100",
    text: "text-amber-600",
    dark: "dark:bg-amber-900/50 dark:text-amber-400",
  },
  {
    bg: "bg-cyan-100",
    text: "text-cyan-600",
    dark: "dark:bg-cyan-900/50 dark:text-cyan-400",
  },
];

export interface ActivityItem {
  title: string;
  detail: string;
}

export type Choice = ActivityItem;

export interface ToolProps {
  id: number;
  title?: string;
  status?: string;
  authorId?: number;
  imageUrl?: string;
  prompt?: string;
  authorName?: string;
  likeCount?: number;
  courseName?: string;
  gradeLevel?: number | string;
  didUserLiked?: boolean;
  description?: string;
  activities?: Choice[];
  videoUrl?: string;
  createdAt?: string;
  contentLanguage?: string;
}

export interface ChoiceBoardCardProps {
  tool: ToolProps;
  isImageLoading?: boolean;
  onCreateAgain?: () => void;
  isEditable?: boolean;
}

export const ChoiceBoardCard = ({
  tool,
  isImageLoading = false,
  onCreateAgain,
  isEditable = false,
}: ChoiceBoardCardProps) => {
  const finalImageUrl = useImagePdf(tool.imageUrl);
  const contentLanguage = tool.contentLanguage || "tr";
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const isAuthor = useIsAuthor(tool.authorId);
  const queryClient = useQueryClient();

  return (
    <div>
      {isAuthor && (
        <MascotBubble
          toolsQueryKey={getGetUserChoiceBoardsQueryKey()}
          toolQueryKey={getGetChoiceBoardQueryKey(tool.id)}
          createChatParams={{
            teacherToolId: tool.id,
            toolType: "CHOICE_BOARD",
          }}
        />
      )}
      <div className="mb-7">
        <CardImage
          courseName={tool?.courseName ?? ""}
          gradeLevel={Number(tool?.gradeLevel) || 0}
          imageUrl={tool?.imageUrl}
          videoUrl={tool?.videoUrl}
          isLoading={isImageLoading}
          createdAt={tool.createdAt}
          theme="green"
          topic={
            <div className="flex items-center gap-4 justify-center">
              <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
                <LayoutGrid size={24} />
              </div>
              <div>
                <Text variant="h1" className="mb-2">
                  {tool?.title || tool?.prompt}
                </Text>
              </div>
            </div>
          }
          contentLanguage={contentLanguage}
        />
        <div className="flex justify-end gap-2 mt-2">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              rounded="xl"
            >
              <RefreshCw size={18} />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}
          <PdfDownloadButton
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            pdfTitle={tool.title || ""}
            document={
              <ChoiceBoardCardPdf
                customizationSettings={customizationSettings}
                tool={{
                  ...tool,
                  imageUrl: finalImageUrl,
                }}
              />
            }
            btnTheme="bg-gradient-to-r from-green-500 to-emerald-600"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 auto-rows-fr relative">
        {tool?.activities?.map((choice, index) => {
          const colorSet = cardColors[index % cardColors.length];
          return (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden h-full"
            >
              <div className="p-4 md:p-6 h-full flex flex-col">
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-xl font-bold text-lg ${colorSet.bg} ${colorSet.text} ${colorSet.dark}`}
                  >
                    {index + 1}
                  </div>
                  <h2
                    className={`text-lg md:text-xl font-bold ${colorSet.text}`}
                  >
                    <EditableMarkdown
                      value={choice?.title}
                      isEditable={isEditable ?? true}
                      handleSave={async (newValue) => {
                        if (!tool.activities) return;

                        const updatedActivities = [...tool.activities];
                        updatedActivities[index] = {
                          ...updatedActivities[index],
                          title: newValue,
                        };

                        await updateChoiceBoard(tool.id, {
                          activities: updatedActivities,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserChoiceBoardsQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetChoiceBoardQueryKey(tool.id),
                        });
                      }}
                    />
                  </h2>
                </div>

                <div className="flex-1">
                  <div className="leading-relaxed">
                    <EditableMarkdown
                      value={choice?.detail}
                      isEditable={isEditable ?? true}
                      handleSave={async (newValue) => {
                        if (!tool.activities) return;

                        const updatedActivities = [...tool.activities];
                        updatedActivities[index] = {
                          ...updatedActivities[index],
                          detail: newValue,
                        };

                        await updateChoiceBoard(tool.id, {
                          activities: updatedActivities,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserChoiceBoardsQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetChoiceBoardQueryKey(tool.id),
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {/* Regenerating overlay */}
        {tool.status === "REGENERATING" && <RegeneratingOverlay />}
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <ChoiceBoardCardPdf
            customizationSettings={customizationSettings}
            tool={{
              ...tool,
              imageUrl: finalImageUrl,
            }}
          />
        }
      />
    </div>
  );
};

export default ChoiceBoardCard;
